import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  CategoryInterface,
  MenuInterface,
  Modifier,
  ModifierGroupInterface,
  ModifierId,
  ProductInterface,
} from '../services/exports/Interfaces';
import collect, { Collection } from 'collect.js';
import CategoryResource from '../services/resources/CategoryResource';
import MenuResource from '../services/resources/MenuResource';
import useImagePreloader from '../hooks/utility/useImagePreloader';
import {
  BOGO_CATEGORY_ID,
  CategoryType,
  EntryType,
  LAST_ORDER_CATEGORY_ID,
  ModifierGroupType,
  ORDER_METHODS,
  OfferKindEnum,
  REDUX_STORE_SLICES,
} from '../services/exports/Constants';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectWorkSchedule } from '../store/slices/InitialDataSlice';
import { StoreInterface } from '../store/types';
import toFlatArray from '../utils/to-flat.util';
import { CompleteBasketItem } from './BasketProvider';
import useScreenType from '../hooks/utility/useScreenType';
import useCategoryRestriction from '../hooks/menu/useCategoryRestriction';
import useGetCompanyMenusApi from '../hooks/api/companies/useGetCompanyMenusApi';
import useBogoItemOffer from '../features/offers/hooks/useBogoItemOffer';
import useGiftCardCategory from '../features/gift-cards/hooks/useGiftCardCategory';
import { isEntryAvailableForChannel } from '../utils/channel-availability';
import { useGetOrderHistoryQuery } from '../store/api-slices';
import useAuth from '../hooks/global/useAuth';
import useAnalytics from '../hooks/analytics/useAnalytics';
import { valueForOrderMethod } from '../utils/on-site';

interface Props {
  children: ReactElement;
}

export interface MenuContextInterface {
  menus: Collection<MenuInterface>;
  selectedMenu: MenuInterface;
  productHeight: number;
  setSelectedMenu: (value: MenuInterface) => void;
  categories: CategoryInterface<WithActualPrice<ProductInterface>>[];
  allCategories: CategoryInterface<WithActualPrice<ProductInterface>>[];
  loadingMenu: boolean;
  compileProduct: (id: number) => CompiledProduct;
  applyOrderMethodPrices: <T extends ProductInterface | Modifier>(item: T) => WithActualPrice<T>;
  getUpsellModifierGroup: (
    basketItems: CompleteBasketItem[],
    openedProduct?: ProductInterface,
  ) => ModifierGroupInterface<CompiledProduct>;
  generateMenuScrollQuery: (categoryId: number, behavior?: ScrollBehavior) => string;
}

export type CompiledProduct = WithActualPrice<ProductInterface> & {
  modifier_groups: ModifierGroupInterface<CompiledModifier | CompiledProduct>[];
};

export type CompiledModifier = WithActualPrice<Modifier> & {
  modifier_groups: ModifierGroupInterface<CompiledModifier>[];
};

export type WithActualPrice<T> = T & {
  actual_price: number;
};

export const MenuContext = React.createContext<MenuContextInterface>(null);

export default function MenuProvider(props: Props) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Providers:MenuProvider' });
  const { trackViewMenu } = useAnalytics();

  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const workSchedule = useSelector(selectWorkSchedule);
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { isLoggedIn } = useAuth();
  const companyId = useSelector((store: StoreInterface) => store[REDUX_STORE_SLICES.INITIAL_DATA].company?.id);

  const { children } = props;
  const { data: menusData } = useGetCompanyMenusApi();

  const { data: orderHistoryData, refetch: refetchOrderHistory } = useGetOrderHistoryQuery(
    { filter: { company_id: companyId }, limit: 1 },
    { skip: !isLoggedIn || !companyId },
  );

  useEffect(() => {
    if (isLoggedIn && companyId && profile?.id) {
      refetchOrderHistory();
    }
  }, [profile?.id]);

  const lastOrder = useMemo(() => orderHistoryData?.data?.[0] ?? null, [orderHistoryData]);

  const hasPreOrder = useSelector(
    (store: StoreInterface) => store[REDUX_STORE_SLICES.INITIAL_DATA].company?.has_pre_order,
  );
  const { preloadImages } = useImagePreloader();
  const { isMobile } = useScreenType();

  const [selectedMenu, setSelectedMenu] = useState(null);
  const giftCardsCategory = useGiftCardCategory();
  const getRestriction = useCategoryRestriction();

  const applyOrderMethodPrices = useCallback(
    <T extends ProductInterface | Modifier>(item: T): WithActualPrice<T> => {
      return {
        ...item,
        // Menus cached before the backend renamed room service may key its prices either way.
        actual_price:
          valueForOrderMethod(item?.order_method_prices, cached_order?.method ?? ORDER_METHODS.PICKUP) ??
          item?.base_price,
      };
    },
    [cached_order?.method],
  );
  const modifierGroupsState = useMemo(() => collect(menusData?.modifier_groups), [menusData?.modifier_groups]);

  const modifierGroups = useMemo(() => {
    return modifierGroupsState.map((item) => {
      const itemModifiers =
        item.modifier_ids
          ?.map((modifier) => setupModifier(modifier, [item.id]))
          .filter((modifier) => isEntryAvailableForChannel(modifier, cached_order?.method)) ?? [];

      return {
        ...item,
        modifiers: itemModifiers,
      };
    });
  }, [modifierGroupsState, menusData?.modifiers, menusData?.products, cached_order?.method]);

  /**
   * `enclosingGroupIds` are the groups this entry sits inside. A menu can nest a group inside
   * itself (an imported menu did: SHOP-WEB-5B), and expanding it again would recurse until the
   * stack runs out and take the whole storefront down. Such a group is left out at the point it
   * would repeat, from both `modifier_group_ids` and `modifier_groups` so the two stay aligned.
   */
  function setupModifier(modifier: ModifierId, enclosingGroupIds: number[]): CompiledProduct | CompiledModifier {
    const entry =
      modifier.type === EntryType.Product
        ? ({ ...menusData?.products[modifier.id] } as CompiledProduct)
        : ({ ...menusData?.modifiers[modifier.id] } as CompiledModifier);

    if (entry?.modifier_group_ids?.length && !entry.modifier_groups?.length) {
      entry.modifier_group_ids = entry.modifier_group_ids.filter((id) => !enclosingGroupIds.includes(id));
      entry.modifier_groups = entry.modifier_group_ids.map((modifierGroupId) => {
        const item = modifierGroupsState.get(modifierGroupId);
        const itemModifiers =
          item?.modifier_ids
            ?.map((modifier) => setupModifier(modifier, [...enclosingGroupIds, modifierGroupId]))
            .filter((modifier) => isEntryAvailableForChannel(modifier, cached_order?.method)) ?? [];

        return {
          ...item,
          modifiers: itemModifiers,
        } as ModifierGroupInterface<CompiledModifier>;
      });
    }

    return applyOrderMethodPrices(entry);
  }

  const compilePrice = <T extends CompiledProduct | CompiledModifier>(product: T): T => {
    return {
      ...product,
      actual_price: product.actual_price,
      modifier_groups: product.modifier_groups?.map((item) => ({
        ...item,
        modifiers: item.modifiers?.map((modifier) => ({
          ...modifier,
          actual_price: modifier.actual_price,
        })),
      })),
    };
  };

  const compileProduct = useCallback(
    (id: number): CompiledProduct => {
      const products = menusData?.products ?? {};
      const data = products[id] ?? null;

      if (!data) {
        return null;
      }

      return compilePrice(
        applyOrderMethodPrices({
          ...data,
          modifier_groups: collect(data?.modifier_group_ids)
            .map((id: number) => modifierGroups.get(id))
            .whereNotNull()
            .toArray(),
        } as CompiledProduct),
      );
    },
    [menusData?.products, modifierGroups, cached_order?.method],
  );

  const channelAvailabilityField = useMemo<'is_available_for_pickup' | 'is_available_for_delivery' | null>(() => {
    if (cached_order?.method === ORDER_METHODS.PICKUP) {
      return 'is_available_for_pickup';
    }

    if (cached_order?.method === ORDER_METHODS.DELIVERY) {
      return 'is_available_for_delivery';
    }

    return null;
  }, [cached_order?.method]);

  const isProductAvailableForChannel = useCallback(
    (product: ProductInterface | undefined): boolean => {
      if (!product) {
        return false;
      }

      if (!channelAvailabilityField) {
        return true;
      }

      return product[channelAvailabilityField] !== false;
    },
    [channelAvailabilityField],
  );

  const mappedCategories = useMemo<Collection<Collection<CategoryInterface<WithActualPrice<ProductInterface>>>>>(
    () =>
      collect(menusData?.menus).mapWithKeys((menu) => [
        menu.id,
        collect(menu.category_ids)
          .map((item: number) => {
            const category = menusData.categories[item];

            if (!category) {
              return null;
            }

            const products =
              category.product_ids
                ?.map((id: number) => menusData?.products[id])
                .filter(isProductAvailableForChannel)
                .map((product) => applyOrderMethodPrices(product)) ?? [];

            return {
              ...category,
              menu_id: menu.id,
              products,
            };
          })
          .whereNotNull()
          .filter((item) => item.products.length > 0)
          // A category is only hidden by its OWN schedule. If it has none, it stays visible even when
          // the menu or the restaurant's work schedule is closed today — restaurant-closed / menu-not-
          // available must not remove categories from the menu (only the category's own schedule does).
          .filter((item) => (hasPreOrder ? true : new CategoryResource(item).isActiveToday())),
      ]),
    [
      menusData?.menus,
      menusData?.categories,
      menusData?.products,
      cached_order?.method,
      hasPreOrder,
      isProductAvailableForChannel,
    ],
  );

  const upsellModifierGroup = useMemo(
    () =>
      modifierGroups.first<ModifierGroupInterface<CompiledProduct>>(
        (modifierGroup) => modifierGroup.type === ModifierGroupType.SystemUpsell,
      ),
    [modifierGroups],
  ) as ModifierGroupInterface<CompiledProduct>;

  const modifierGroupsToFlatModifiers = (
    modifierGroups: ModifierGroupInterface<CompiledModifier>[],
    result: (Modifier | ProductInterface)[] = [],
  ): (Modifier | ProductInterface)[] => {
    if (!modifierGroups?.length) {
      return result;
    }

    const nextData: ModifierGroupInterface<CompiledModifier>[] = [];

    modifierGroups.forEach((modifierGroup) => {
      if (!modifierGroup.modifier_ids?.length) {
        return;
      }

      result.push(...modifierGroup.modifiers);

      modifierGroup.modifiers.forEach((modifier) => {
        if (!modifier.modifier_group_ids?.length) {
          return;
        }

        nextData.push(...modifier.modifier_groups);
      });
    });

    return modifierGroupsToFlatModifiers(nextData, result);
  };

  const getUpsellModifierGroup = useCallback(
    (addedItems: CompleteBasketItem[], openedProduct?: CompiledProduct): ModifierGroupInterface<CompiledProduct> => {
      if (!upsellModifierGroup?.modifiers?.length) {
        return;
      }

      const flatAddedItems = addedItems?.length ? collect(toFlatArray(addedItems, 'modifiers')) : collect();

      const openedProducts = collect(
        modifierGroupsToFlatModifiers(openedProduct?.modifier_groups as ModifierGroupInterface<CompiledModifier>[]),
      ).where('type', EntryType.Product);
      const openedProductCategoryIds = openedProducts
        .pluck('category_ids')
        .collapse()
        .when(!!openedProduct?.category_ids?.length, (collection) => collection.concat(openedProduct?.category_ids));
      const openedProductNames = openedProducts
        .pluck('name')
        .when(!!openedProduct, (collection) => collection.push(openedProduct.name));

      const addedProducts = flatAddedItems.where('entry.type', EntryType.Product);
      const avoidedProductCategoryIds = addedProducts
        .pluck('entry.category_ids')
        .collapse()
        .concat(openedProductCategoryIds)
        .unique()
        .toArray();
      const avoidedProductNames = addedProducts.pluck('entry.name').concat(openedProductNames).unique().toArray();
      const productsByMenu = mappedCategories.get(selectedMenu?.id)?.pluck('product_ids').collapse().toArray() ?? [];

      const modifiers = collect(upsellModifierGroup.modifiers)
        .filter((modifier) => !avoidedProductNames.includes(modifier.name))
        .filter((modifier) => productsByMenu.includes(modifier.id))
        .filter(
          (modifier: ProductInterface) =>
            !modifier.category_ids?.some((categoryId) => avoidedProductCategoryIds.includes(categoryId)),
        )
        .filter((modifier) => modifier.is_available)
        .filter((modifier: ProductInterface) => !getRestriction(modifier.category_ids).isRestricted)
        .unique('id')
        .toArray() as CompiledProduct[];

      if (modifiers.length > 2) {
        modifiers.length = 2;
      }

      return {
        ...upsellModifierGroup,
        modifiers,
      };
    },
    [modifierGroups, selectedMenu?.id, mappedCategories, getRestriction],
  );

  const menus = useMemo(() => {
    const menus = collect(menusData?.menus).filter((item) => mappedCategories.get(item?.id)?.isNotEmpty());

    return menus.isEmpty() ? menus.push(collect(menusData?.menus).first()) : menus;
  }, [menusData?.menus, mappedCategories]);

  const bogoItemOffer = useBogoItemOffer();

  const bogoCategory: CategoryInterface<WithActualPrice<ProductInterface>> = useMemo(() => {
    if (!bogoItemOffer.offer || bogoItemOffer.offer.kind !== OfferKindEnum.Bogo) {
      return;
    }

    const bogoProducts = bogoItemOffer.offer.details.products
      .map((productId) => compileProduct(productId))
      ?.filter((product) => Boolean(product));

    if (!bogoProducts?.length) {
      return;
    }

    return {
      id: BOGO_CATEGORY_ID,
      menu_id: null,
      menu_ids: [],
      type: CategoryType.Bogo,
      name: t('labels.bogo'),
      image_url: null,
      product_ids: [],
      products: bogoProducts,
    };
  }, [bogoItemOffer.offer, menusData?.products]);

  const lastOrderCategory = useMemo<CategoryInterface<WithActualPrice<ProductInterface>> | null>(() => {
    if (!lastOrder?.line_items?.length) {
      return null;
    }

    const products = lastOrder.line_items
      .map((lineItem) => {
        const compiled = compileProduct(lineItem.product_id);

        if (!compiled || !isProductAvailableForChannel(compiled)) {
          return null;
        }

        // Prices stay the product's current compiled prices (same as the regular menu card). The line
        // item's historical amounts include its modifiers and any one-off discount of that past order
        // (e.g. a loyalty reward item), so they would advertise a price that is never charged again.
        return {
          ...compiled,
          order_line_item: {
            product_id: lineItem.product_id,
            quantity: 1,
            modifiers: lineItem.modifiers,
            note: lineItem.note,
          },
        } as WithActualPrice<ProductInterface>;
      })
      .filter(Boolean) as WithActualPrice<ProductInterface>[];

    if (products.length === 0) {
      return null;
    }

    // A past order can contain items that cannot be ordered right now — sold out, or in a category
    // whose schedule is closed (breakfast at dinner time). They stay in the list, greyed out exactly
    // like on the menu (see CategoryItem), but an "order again" category where nothing is orderable
    // is pure noise, so it is dropped entirely.
    const hasOrderableProduct = products.some(
      (product) => product.is_available !== false && !getRestriction(product.category_ids).isRestricted,
    );

    if (!hasOrderableProduct) {
      return null;
    }

    return {
      id: LAST_ORDER_CATEGORY_ID,
      menu_id: null,
      menu_ids: [],
      type: CategoryType.LastOrder,
      name: `🔄 ${t('labels.order_again')}`,
      image_url: null,
      product_ids: products.map((product) => product.id),
      products,
    };
  }, [lastOrder, compileProduct, isProductAvailableForChannel, getRestriction, t]);

  const allCategories = useMemo<CategoryInterface<WithActualPrice<ProductInterface>>[]>(() => {
    return [
      ...(lastOrderCategory ? [lastOrderCategory] : []),
      ...(bogoCategory ? [bogoCategory] : []),
      ...((mappedCategories.get(selectedMenu?.id)?.toArray() as CategoryInterface<
        WithActualPrice<ProductInterface>
      >[]) ?? []),
      ...(giftCardsCategory ? [giftCardsCategory] : []),
    ];
  }, [selectedMenu, mappedCategories, i18n.language, bogoCategory, giftCardsCategory, lastOrderCategory]);

  const categories = useMemo<CategoryInterface<WithActualPrice<ProductInterface>>[]>(() => {
    const hasMostPopular = collect(allCategories).where('type', CategoryType.MostPopular).isNotEmpty();

    return collect(allCategories)
      .where('type', '!=', CategoryType.MostPopular)
      .when(hasMostPopular, (collection) => collection.where('type', '!=', CategoryType.Favourites))
      .toArray();
  }, [allCategories]);

  // Picked from each menu's schedule rather than the response's `is_active`: the menus response is
  // cached for hours, so `is_active` reflects the moment it was cached, not now.
  const defaultMenu = () =>
    menus.first((menu: MenuInterface) =>
      new MenuResource(menu, workSchedule).isActive(cached_order?.scheduled_for ?? null),
    ) ?? menus.first();

  useEffect(() => {
    if (!selectedMenu) {
      setSelectedMenu(defaultMenu());
    }
  }, [menus]);

  useEffect(() => {
    if (!menus.firstWhere('id', selectedMenu?.id)) {
      setSelectedMenu(defaultMenu());
    }
  }, [menus]);

  // `trackViewMenu` changes identity once the analytics SDKs are initialised after consent, so this
  // re-runs and view_menu still fires on the page the visitor accepted on. Its once-per-session flag
  // prevents duplicates.
  useEffect(() => {
    menusData?.categories && trackViewMenu(menusData?.categories);
  }, [menusData?.categories, trackViewMenu]);

  useEffect(() => {
    if (selectedMenu) {
      preloadImages(
        collect(categories)
          .flatMap((category) => category?.products)
          .pluck('thumbnail_url')
          .filter((url: null | string) => !!url)
          .toArray(),
      );
    }
  }, [selectedMenu]);

  const productHeight = useMemo(() => {
    return isMobile ? 145 : 190;
  }, [isMobile]);

  const generateMenuScrollQuery = (categoryId: number, behavior: ScrollBehavior = 'smooth'): string => {
    return `?categoryId=${categoryId}&behavior=${behavior}`;
  };

  const context: MenuContextInterface = {
    menus,
    selectedMenu,
    productHeight,
    setSelectedMenu,
    categories,
    allCategories,
    compileProduct,
    applyOrderMethodPrices,
    getUpsellModifierGroup,
    generateMenuScrollQuery,
    loadingMenu: menusData === undefined,
  };

  return <MenuContext.Provider value={context}>{children}</MenuContext.Provider>;
}
